import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import { useGetStaffPageMutation } from '../../api/reports/ReportApi';
import { DiscountLine, StaffRow } from '../../types/reports';
import { longDate } from '../../utils/dates';
import { exportToExcel } from '../../utils/exportExcel';
import SearchField from '../common/SearchField';

const money = (value?: number | null) => Number(value || 0).toFixed(2);

const headCell = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  px: 1.5,
  py: 1,
};

const monthStart = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Who sold what, and what they gave away doing it.
 *
 * <p>This is not about catching anybody out - a counter that can discount has to be
 * able to discount, and a phone sold at cost to shift old stock is a decision, not a
 * mistake. It is about the owner being able to ask the question at all, which was
 * impossible until every record carried the signed-in user.
 *
 * <p>Two tables: a line per person, then the individual lines that were discounted
 * or sold below cost, so a number that looks wrong can be traced to the bill behind
 * it.
 */
const StaffReport = () => {
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [searchValue, setSearchValue] = useState('');

  const [rows, setRows] = useState<StaffRow[]>([]);
  const [lines, setLines] = useState<DiscountLine[]>([]);
  const [lineCount, setLineCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [getStaffPage] = useGetStaffPageMutation();

  useEffect(() => {
    if (timeout.current) clearTimeout(timeout.current);
    setLoading(true);
    timeout.current = setTimeout(async () => {
      try {
        const response = await getStaffPage({
          searchValue,
          fromDate,
          toDate,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }).unwrap();
        setRows(response?.rows || []);
        setLines(response?.data || []);
        setLineCount(response?.totalCount || 0);
      } catch {
        setRows([]);
        setLines([]);
        setLineCount(0);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, fromDate, toDate, paginationModel.page, paginationModel.pageSize]);

  /**
   * Any change of filter puts the list back on the first page.
   *
   * <p>Page 4 of the old results is not page 4 of the new ones - usually it is past
   * the end of them, and the shop is shown an empty grid for a search that matched.
   */
  useEffect(() => {
    setPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, fromDate, toDate]);

  const belowCost = lines.filter((line) => line.belowCost);
  const totalDiscount = rows.reduce((sum, row) => sum + Number(row.discountGiven || 0), 0);
  const totalLoss = belowCost.reduce((sum, line) => sum + Number(line.lossPerPiece || 0) * Number(line.quantity || 0), 0);

  const lineColumns: GridColDef[] = [
    { field: 'billNo', headerName: 'Bill Number', flex: 1, minWidth: 140 },
    { field: 'billDate', headerName: 'Date', flex: 1, minWidth: 120, renderCell: (row) => longDate(row.value as string) },
    { field: 'soldBy', headerName: 'Sold by', flex: 1, minWidth: 110 },
    { field: 'customerName', headerName: 'Customer', flex: 1, minWidth: 150 },
    { field: 'productName', headerName: 'Product', flex: 1.4, minWidth: 200 },
    { field: 'purchasePrice', headerName: 'Cost', flex: 1, minWidth: 100, renderCell: (row) => money(row.value as number) },
    {
      field: 'rate',
      headerName: 'Sold at',
      flex: 1,
      minWidth: 100,
      renderCell: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {money(row.value as number)}
        </Typography>
      ),
    },
    { field: 'discountAmount', headerName: 'Discount', flex: 1, minWidth: 100, renderCell: (row) => money(row.value as number) },
    {
      field: 'belowCost',
      headerName: 'Loss',
      flex: 1,
      minWidth: 110,
      renderCell: (row) =>
        row.value ? (
          <Chip size="small" color="error" label={money(Number(row.row.lossPerPiece) * Number(row.row.quantity))} />
        ) : (
          '-'
        ),
    },
  ];

  const download = () =>
    exportToExcel(
      rows.map((row) => ({
        User: row.user,
        Bills: row.bills,
        Sales: row.sales,
        'Discount given': row.discountGiven,
        'Cancelled bills': row.cancelledBills,
        'Cancelled value': row.cancelledValue,
        'Lines below cost': row.belowCostLines,
      })),
      `staff-${fromDate}-to-${toDate}`,
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <SearchField value={searchValue} onChange={setSearchValue} placeholder="Any words: bill no, user, customer, product..." width={300} />
        <TextField
          label="From"
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(event) => setFromDate(event.target.value)}
        />
        <TextField
          label="To"
          size="small"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(event) => setToDate(event.target.value)}
        />
        <Button
          size="small"
          onClick={() => {
            setFromDate(monthStart());
            setToDate(today());
          }}
        >
          This month
        </Button>
        <Chip variant="outlined" color="warning" label={`${money(totalDiscount)} discount given`} />
        {belowCost.length > 0 && <Chip color="error" label={`${belowCost.length} line(s) below cost · ${money(totalLoss)}`} />}
        <Box flexGrow={1} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      {rows.length === 0 && !loading && (
        <Alert severity="info">
          {searchValue ? `Nothing matching "${searchValue}" in this period.` : 'No bills in this period.'}
        </Alert>
      )}

      {rows.length > 0 && (
        <TableContainer component={Card}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['User', 'Bills', 'Sales', 'Discount given', 'Cancelled', 'Cancelled value', 'Below cost'].map((head) => (
                  <TableCell key={head} sx={headCell} align={head === 'User' ? 'left' : 'right'}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.user} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{row.user}</TableCell>
                  <TableCell align="right">{row.bills}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {money(row.sales)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: Number(row.discountGiven) > 0 ? 'warning.main' : 'text.secondary' }}>
                    {money(row.discountGiven)}
                  </TableCell>
                  <TableCell align="right">{row.cancelledBills}</TableCell>
                  <TableCell align="right" sx={{ color: Number(row.cancelledValue) > 0 ? 'error.main' : 'text.secondary' }}>
                    {money(row.cancelledValue)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: Number(row.belowCostLines) > 0 ? 'error.main' : 'text.secondary' }}>
                    {row.belowCostLines}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ------------------------------------------- the lines behind it */}
      <Card sx={{ mt: 3, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              DISCOUNTED AND BELOW-COST LINES
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Every line where money came off the price, or the piece went out for less than it cost
            </Typography>
          </Box>
          <Chip variant="outlined" label={`${lineCount} line(s)`} />
        </Stack>
        <Divider sx={{ my: 1.5 }} />

        <DataGrid
          columns={lineColumns}
          rows={lines}
          getRowId={(row) => `${row.billNo}-${row.productName}-${row.rate}`}
          rowCount={lineCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(model: GridPaginationModel) => setPaginationModel(model)}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          loading={loading}
          sx={{ height: '50vh' }}
        />
      </Card>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        Bills entered before the software started recording the signed-in user are grouped under "Not recorded" - every
        bill saved from now on carries the person who saved it.
      </Typography>
    </Box>
  );
};

export default StaffReport;
